/**
 * Gatilho da pesquisa de NPS: decide QUANDO o card pode aparecer.
 *
 * DOIS MODOS, escolhidos pela rota (`modoDaRota` em constants/eventos.js):
 *
 * EVENTO — módulos com tarefa concluível (comissões, tesouraria, CRM…). O
 *   card só pode aparecer depois que o handler de sucesso da tarefa emitir o
 *   evento de negócio (`aoEvento`), e ainda assim espera ESPERA_POS_EVENTO_MS
 *   de tela sossegada — modal fechado, snack mostrado, rede parada. Um evento
 *   por sessão de módulo; evento não consumido em EVENTO_VALIDADE_MS expira.
 *
 * PERMANÊNCIA — módulos só de leitura (home, BI, visão geral). Quatro
 *   condições ao mesmo tempo: rota estável, rede em silêncio por SILENCIO_MS,
 *   sem erro visível, e PERMANENCIA_MS de uso do módulo JÁ CARREGADO.
 *
 * Por que dois modos: a primeira versão era só permanência (15 s) e, em
 * produção, deu 37 dispensas e 0 respostas — 31 no meio do fechamento de
 * comissão. Tempo parado numa tela de trabalho não é fim de tarefa.
 *
 * Só depois disso a elegibilidade é perguntada ao servidor, que aplica as
 * travas de 30/60/90 dias e o gate do usuário interno.
 */
import { ehInterno, idDoProfissional } from '@modules/nps/services/nps-usuario'
import { moduloDaRota } from '@modules/nps/constants/modulos'
import {
  MODO_EVENTO,
  MODO_PERMANENCIA,
  modoDaRota,
  moduloDoEvento,
  rotaQueConfirma,
} from '@modules/nps/constants/eventos'
import { podePerguntar } from '@modules/nps/services/nps-storage'
import { requisicoesEmVoo as requisicoesEmVooPadrao } from '@modules/nps/services/nps-rede'
import apiNps from '@modules/nps/api/apiNps'

/**
 * Modo de teste: `?nps_forcar=<modulo>` na URL pula as janelas de 30/60/90
 * dias, a amostragem, a trava local e o tempo de permanência — mas NÃO pula a
 * espera pela tela carregar.
 *
 * Isso é deliberado: o card abrindo sobre um módulo a meio carregar não é o
 * comportamento que se quer validar, é justamente o que o gatilho existe para
 * evitar. Então o modo de teste continua exigindo rede em silêncio e ausência
 * de estado de erro/carregando — o que ele economiza é o tempo, não a checagem.
 *
 * Para o caso raro de querer o card imediatamente (depurar o próprio card, com
 * a tela já pronta), `?nps_forcar=<modulo>&nps_agora=1` dispensa também essa
 * espera.
 *
 * O servidor decide se atende: o `forcar=1` só tem efeito onde
 * `TEST_LOCAL_ROUTES_ENABLED` está ligado (TEST_LOCAL=true e AMBIENTE !=
 * PRODUCAO). Em produção o parâmetro é aceito e ignorado — a URL não vira
 * atalho para furar as janelas de 30/60/90 dias.
 */
export const PARAM_FORCAR = 'nps_forcar'

export const PARAM_AGORA = 'nps_agora'

function paramDaUrl(nome) {
  if (typeof globalThis.location === 'undefined') return null
  const busca = globalThis.location.search || ''
  const encontrado = new RegExp('[?&]' + nome + '=([^&]+)').exec(busca)
  return encontrado ? decodeURIComponent(encontrado[1]) : null
}

export function moduloForcado() {
  return paramDaUrl(PARAM_FORCAR)
}

/**
 * Último motivo de o card não aparecer, para inspeção no console.
 *
 * Existe porque `drop_console` apaga os `console.*` no build de produção — sem
 * isto, "não apareceu nada" fica indistinguível de "a chamada nem saiu".
 */
export function registrarMotivo(resultado) {
  globalThis.__npsUltimoMotivo = resultado
    ? resultado.motivo || 'elegivel_false_sem_motivo'
    : 'sem_resposta_do_servidor'
  return globalThis.__npsUltimoMotivo
}

/** Dispensa até a espera pela tela carregar. Só para depurar o próprio card. */
export function ignorarEsperaDeCarga() {
  return paramDaUrl(PARAM_AGORA) === '1'
}

export const SILENCIO_MS = 2000
// Era 15 s. Só vale para módulos de leitura agora, e 15 s de leitura ainda é
// "no meio de" — 60 s é uso de verdade.
export const PERMANENCIA_MS = 60000
// Silêncio contínuo depois do evento: o handler ainda fecha modal, mostra
// snack e recarrega a lista. `momentoImproprio()` segura enquanto há diálogo
// aberto ou requisição em voo; estes 4 s são a folga depois disso.
export const ESPERA_POS_EVENTO_MS = 4000
// Evento aceito mas não consumido (a tela nunca sossegou, a aba foi
// minimizada) expira: o card não pode cair "do nada" minutos depois.
export const EVENTO_VALIDADE_MS = 120000
const INTERVALO_CHECAGEM_MS = 1000
export const GATILHO_FORCADO = 'forcado'

/**
 * Elementos que denunciam tela em erro ou ainda carregando. Se qualquer um
 * estiver presente, o relógio não anda: a nota mediria o incidente.
 *
 * Os seletores foram conferidos contra o que os componentes REALMENTE
 * renderizam — `feedback-wrapper.vue` emite `.feedback-content` no estado de
 * erro/vazio e `_base-loading-skeleton.vue` emite `.base-loading-skeleton`.
 * Uma versão anterior listava `.base-loading` e `[data-erro]`, que não existem
 * em lugar nenhum do app: a guarda ficava inerte justamente no estado de erro
 * mais comum, e o teste passava porque inventava a marcação.
 */
const SELETORES_DE_ERRO = [
  '.base-loading-skeleton',
  '.v-skeleton-loader',
  '.feedback-content',
]

/**
 * Painéis que ocupam o mesmo canto. Com qualquer um aberto, o card não
 * aparece — ninguém precisa de pesquisa por cima de uma conversa.
 */
const SELETORES_CONCORRENTES = [
  '.has-ff-ia-panel',
  '.ff-ia-panel',
  // `.chat-ia-pop-up` (sem sufixo) é o BALÃO de 71x71 px que fica sempre na
  // tela de quem tem o chat de IA — não uma conversa. Uma versão anterior
  // listava o balão, e em escritório com a flag `AAWZ Intelligence` o card
  // nunca aparecia, por nenhum dos dois modos. Medido no whitelabelh em
  // 15/09/2026: `telaOcupada()` true com a tela parada e nada aberto. O que
  // ocupa o canto é a conversa aberta, `.chat-ia-pop-up-messages`.
  '.chat-ia-pop-up-messages',
  // Diálogo Vuetify aberto: com z-index 200 o card brigaria com o overlay.
  '.v-dialog--active',
]

function existeNoDom(seletores) {
  if (typeof document === 'undefined' || !document.querySelector) return false
  return seletores.some((seletor) => document.querySelector(seletor))
}

export function telaOcupada() {
  return existeNoDom(SELETORES_DE_ERRO) || existeNoDom(SELETORES_CONCORRENTES)
}

function estadoInicial() {
  return {
    modulo: null,
    modo: null, // 'evento' | 'permanencia' | null — decidido pela rota
    gatilho: null, // o que armou o relógio: 'evento' | 'permanencia' | 'forcado'
    silencioDesde: null,
    prontoDesde: null,
    eventoDesde: null,
    // Evento cuja conclusão é uma navegação (simulador): { modulo, rota }.
    eventoAguardandoRota: null,
    // Módulo em que o evento já pediu a pesquisa nesta sessão de módulo —
    // aprovar dois fechamentos seguidos não pode perguntar duas vezes.
    moduloConsumido: null,
    timer: null,
    disparou: false,
    forcado: false,
  }
}

// ---------------------------------------------------------------------------
// Funções puras sobre `estado`. Vivem fora do closure para o `criarGatilho`
// caber no limite de statements do lint sem sacrificar nomes.
// ---------------------------------------------------------------------------

function pararRelogio(estado) {
  if (estado.timer) {
    clearInterval(estado.timer)
    estado.timer = null
  }
}

function zerarJanelas(estado) {
  estado.silencioDesde = null
  estado.prontoDesde = null
}

function esquecerDisparoAnterior(estado) {
  pararRelogio(estado)
  zerarJanelas(estado)
  estado.disparou = false
  estado.gatilho = null
  estado.eventoDesde = null
}

function resolverRota(estado, nomeDaRota) {
  // Rota fora do mapa não dispara pesquisa — nem gasta relógio.
  const moduloNovo = moduloDaRota(nomeDaRota)
  // Um evento por SESSÃO DE MÓDULO: navegar entre telas do mesmo módulo
  // não reabre a chance; trocar de módulo, sim.
  if (moduloNovo !== estado.moduloConsumido) estado.moduloConsumido = null
  estado.modulo = moduloNovo
  estado.modo = modoDaRota(nomeDaRota)
}

/**
 * Marca o começo de uma janela e diz se ela já fechou. Serve às duas:
 * `silencioDesde` (rede parada — precisa ser CONTÍNUO, senão o card
 * apareceria entre duas requisições) e `prontoDesde` (tempo de uso do módulo
 * JÁ CARREGADO, não de espera de loader).
 */
function janelaCompletou(estado, campo, duracao, instante) {
  if (estado[campo] === null) {
    estado[campo] = instante
    return false
  }
  return instante - estado[campo] >= duracao
}

/** As janelas que faltam, conforme o que armou o relógio. */
function janelaFechou(estado, instante) {
  if (estado.gatilho === MODO_EVENTO) {
    return janelaCompletou(
      estado,
      'silencioDesde',
      ESPERA_POS_EVENTO_MS,
      instante
    )
  }
  if (!janelaCompletou(estado, 'silencioDesde', SILENCIO_MS, instante))
    return false
  // A permanência mede uso do módulo já carregado — no modo de teste ela é o
  // único tempo que se economiza.
  if (estado.forcado) return true
  return janelaCompletou(estado, 'prontoDesde', PERMANENCIA_MS, instante)
}

function eventoExpirou(estado, instante) {
  if (estado.gatilho !== MODO_EVENTO || estado.forcado) return false
  return instante - estado.eventoDesde > EVENTO_VALIDADE_MS
}

/**
 * Aba oculta não conta como uso: o relógio é de parede, então uma aba
 * esquecida numa rota mapeada satisfaria silêncio + permanência sem ninguém
 * usando o módulo — a nota mediria o abandono, não o produto.
 */
/**
 * Modo apresentação do BI (dashboard em tela cheia numa TV): o card nem seria
 * visível — a Fullscreen API só renderiza o elemento em tela cheia — mas o
 * gatilho gravaria "exibido" e ninguém poderia responder nem dispensar.
 */
function emTelaCheia() {
  if (typeof document === 'undefined') return false
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  )
}

function momentoImproprio(requisicoesEmVoo) {
  if (typeof document !== 'undefined' && document.hidden) return true
  if (emTelaCheia()) return true
  return requisicoesEmVoo() > 0 || telaOcupada()
}

export function criarGatilho(opcoes) {
  const aoFicarElegivel = opcoes.aoFicarElegivel
  const usuario = opcoes.usuario
  // Injetável: export de módulo ES não é substituível por spy, e o contador de
  // rede é justamente o que precisa variar no teste do gatilho.
  const requisicoesEmVoo = opcoes.requisicoesEmVoo || requisicoesEmVooPadrao
  // Relógio injetável pelo mesmo motivo: o jest deste repo (24) não mocka
  // `Date.now`, então sem isto as janelas de silêncio/permanência seriam
  // intestáveis — e são exatamente a regra que precisa de teste.
  const agora = opcoes.agora || Date.now
  const estado = estadoInicial()

  function ligarRelogio() {
    if (!estado.timer) {
      estado.timer = setInterval(avaliar, INTERVALO_CHECAGEM_MS)
    }
  }

  function reiniciar(nomeDaRota) {
    esquecerDisparoAnterior(estado)

    const forcado = moduloForcado()
    estado.forcado = !!forcado
    if (forcado) {
      estado.modulo = forcado
      armarModoDeTeste()
      return
    }

    resolverRota(estado, nomeDaRota)
    if (eventos.chegouARotaAguardada(nomeDaRota)) return

    // No modo evento a rota sozinha não arma nada — é o `aoEvento` que arma.
    if (estado.modo === MODO_PERMANENCIA && podeSeguir()) {
      estado.gatilho = MODO_PERMANENCIA
      ligarRelogio()
    }
  }

  /**
   * No modo de teste o card ainda espera a tela ficar pronta — o que se
   * economiza é a permanência e as janelas de frequência, não a checagem de
   * carregamento. `nps_agora=1` dispensa até isso, para depurar o card em si.
   */
  function armarModoDeTeste() {
    estado.gatilho = GATILHO_FORCADO
    if (ignorarEsperaDeCarga()) {
      estado.disparou = true
      perguntarAoServidor(estado.modulo, true)
      return
    }
    ligarRelogio()
  }

  function podeSeguir() {
    if (estado.disparou || !estado.modulo) return false
    // No modo de teste a trava local não vale — quem decide é o servidor, que
    // recebe `forcar=1` e responde conforme o ambiente.
    if (estado.forcado) return true
    // Revalida as mesmas guardas de `reiniciar`. Elas já barram a criação do
    // relógio, mas repetir aqui evita que uma chamada isolada de `avaliar`
    // (relógio remanescente, chamada direta) mostre a pesquisa a quem não
    // deveria vê-la — em especial ao usuário interno da AAWZ.
    const dados = usuario()
    const idProfissional = idDoProfissional(dados)
    if (!dados || ehInterno(dados) || !idProfissional) return false
    return podePerguntar(idProfissional, estado.modulo)
  }

  /**
   * Fluxo do modo EVENTO. A chave já vem normalizada (ver `chaveDoEvento`);
   * quem decide se é uma conclusão é a allowlist, não quem chama. Fora da
   * allowlist, fora da rota do módulo, já consumido ou sem permissão: ignora
   * em silêncio — nunca pode atrapalhar o handler que emitiu.
   */
  const eventos = {
    receber(chave) {
      if (!chave) return
      const modulo = moduloDoEvento(chave, estado.modulo)
      if (!modulo || !eventos.podeArmar(modulo)) return
      const rotaQueConfirmaEste = rotaQueConfirma(chave)
      if (rotaQueConfirmaEste) {
        estado.eventoAguardandoRota = { modulo, rota: rotaQueConfirmaEste }
        return
      }
      eventos.armar(modulo)
    },
    /** Um por sessão de módulo, e só com a trava local liberada. */
    podeArmar(modulo) {
      return estado.moduloConsumido !== modulo && podeSeguir()
    },
    armar(modulo) {
      estado.modulo = modulo
      estado.gatilho = MODO_EVENTO
      estado.eventoDesde = agora()
      zerarJanelas(estado)
      ligarRelogio()
    },
    /** Simulador: o evento armou; a chegada em `simulador.analise` confirma. */
    chegouARotaAguardada(nomeDaRota) {
      const aguardando = estado.eventoAguardandoRota
      estado.eventoAguardandoRota = null
      if (!aguardando) return false
      if (
        aguardando.rota !== nomeDaRota ||
        aguardando.modulo !== estado.modulo
      ) {
        return false
      }
      // A mesma guarda de `receber`: uma segunda simulação na mesma sessão
      // chegava aqui sem passar por `moduloConsumido` e perguntava de novo.
      if (!eventos.podeArmar(aguardando.modulo)) return false
      eventos.armar(aguardando.modulo)
      return true
    },
  }

  function avaliar() {
    // Sem gatilho armado não há o que avaliar. Sem esta guarda, uma chamada
    // isolada (relógio remanescente, `_avaliar` direto) cairia na permanência
    // mesmo numa rota de modo evento — o furo que se quer fechar.
    if (!estado.gatilho || !podeSeguir()) return

    const instante = agora()
    if (eventoExpirou(estado, instante)) {
      pararRelogio(estado)
      estado.gatilho = null
      globalThis.__npsUltimoMotivo = 'evento_expirou'
      return
    }

    if (momentoImproprio(requisicoesEmVoo)) {
      zerarJanelas(estado)
      return
    }

    if (janelaFechou(estado, instante)) disparar()
  }

  function disparar() {
    estado.disparou = true
    if (estado.gatilho === MODO_EVENTO) estado.moduloConsumido = estado.modulo
    pararRelogio(estado)
    perguntarAoServidor(estado.modulo, estado.forcado)
  }

  function perguntarAoServidor(modulo, forcar) {
    const gatilho = estado.gatilho || MODO_PERMANENCIA
    apiNps.elegibilidade(modulo, forcar).then(function (resultado) {
      if (resultado && resultado.elegivel) {
        aoFicarElegivel({
          modulo: modulo,
          pergunta: resultado.pergunta,
          rodape: resultado.rodape,
          gatilho: gatilho,
        })
        return
      }

      // O build de produção remove `console.*` (drop_console no vue.config),
      // então log de diagnóstico não sobrevive no preview. Guardar o motivo
      // aqui é o que permite descobrir por que o card não apareceu:
      // `window.__nps.ultimoMotivo`.
      registrarMotivo(resultado)

      // `nps_agora=1` promete o card na tela — e o servidor pode recusar por
      // algo que o modo de teste NÃO pula (sessão sem profissional, usuário
      // interno). Nesse caso o card é montado assim mesmo, para dar para
      // validar a interface; a gravação segue valendo as regras do servidor.
      if (ignorarEsperaDeCarga()) {
        aoFicarElegivel({
          modulo: modulo,
          pergunta: 'Que nota você dá para ' + modulo + '?',
          rodape: 'Modo de teste — o servidor recusou a exibição.',
          gatilho: GATILHO_FORCADO,
        })
      }
    })
  }

  return {
    aoNavegar: reiniciar,
    aoEvento: eventos.receber,
    parar: function () {
      pararRelogio(estado)
    },
    // expostos para teste
    _avaliar: avaliar,
    _estado: function () {
      return estado
    },
  }
}
